import React, { useEffect, useState } from "react";
import Dropdown from "../dropdown";
import { Dropdown as ReactDropdown } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchStreamAccessUsers,
  fetchUserAccess,
  selectStreamForAccessManagement,
  updateStreamAccess
} from "../../redux/actions/streamAccessController";

import { User, Users } from "../../types/streamAccessControl";
import { StreamDetail } from "../../pages/streams/types";

import Slider from "../slider";
import Button from "../button";
import Tooltip from "../tooltip";
import Truncate from "../truncate";
import InfiniteScroll from "../infiniteScroll";
import { fetchStream } from "src/redux/actions/streams";
import PlaceholderEmptyMessage from "src/components/placeholderEmptyMessage"
import StreamAccessEmptyMessageSvgIcon from "src/legacy/images/streamAccessManagementEmptyMessage.svg";
import { IUseFetchProps, useFetch } from "../hooks/useFetch";
import { useStreamsAdoption } from "src/redux/actions/adoption/streams";

import _ from "lodash";
import cx from "classnames";
import styles from "./manageUserAccess.module.scss";

interface ManagerUserAccessProps {
  openUserAccess: boolean;
  stream: StreamDetail;
  handleClose?: () => void;
}

const ManageUserAccess: React.FC<ManagerUserAccessProps> = ({
  openUserAccess,
  stream,
  handleClose,
}) => {
  const dispatch = useDispatch();
  const { trackUserAccessUpdated, trackManageUserAccessViewed } = useStreamsAdoption();

  const [updatedAccess, setUpdatedAccess] = useState<{ [key: string]: string }>({});
  const [selectedOption, setSelectedOption] = useState("");
  const [query, setQuery] = useState("");
  const [updatedUsers, setUpdatedUsers] = useState<Users[]>([]);
  const [updatedUserGroups, setUpdatedUserGroups] = useState<Users[]>([]);

  const loadingUsers = useSelector((state: any) =>
    _.get(state, "streamAccessControl.stream.userAccess.loading", true)
  );
  const users: Users[] = useSelector((state: any) =>
    _.get(state, "streamAccessControl.stream.userAccess.items", [])
  );
  const loadMore = useSelector((state: any) => _.get(state, "streamAccessControl.stream.userAccess.loadMore", false));
  const currentPage = useSelector((state: any) => _.get(state, "streamAccessControl.stream.userAccess.currentPage", 0));
  const totalPages = useSelector((state: any) => _.get(state, "streamAccessControl.stream.userAccess.totalPages", 0));
  const confirming: boolean = useSelector((state: any) => _.get(state, "streamAccessControl.stream.updatingUserAccess", false));
  const updated: boolean = useSelector((state: any) => _.get(state, "streamAccessControl.stream.updatedUserAccess", false));
  const updatedUserAccess: boolean = useSelector((state: any) => _.get(state, "streamAccessControl.stream.updatedUserAccess", {}));
  const streamAccessControlStream: any = useSelector((state: any) => _.get(state, "streamAccessControl.stream", null));

  const updateStreamAccessProps: IUseFetchProps = {
    action: updateStreamAccess,
    loading: confirming,
    succeeded: updated,
    response: [],
    error: null
  };

  const { handleFetch: updateStreamAccessRequest } = useFetch(updateStreamAccessProps);

  useEffect(() => {
    if(_.size(users) === 0 && !_.get(streamAccessControlStream, "id")) {
      dispatch(selectStreamForAccessManagement(stream));
      setSelectedOption(stream.writeAll ? "edit" : stream.readAll ? "read" : "");
    }
    trackManageUserAccessViewed();
  }, [])

  useEffect(() => {
      dispatch(selectStreamForAccessManagement(stream));
      setSelectedOption(stream.writeAll ? "edit" : stream.readAll ? "read" : "");
      if(stream.writeAll) {
        bulkUpdateAccess("edit");
      }
      if(stream.readAll) {
        bulkUpdateAccess("read"); 
      }
  }, [stream.id])
  

  useEffect(() => {
    const request = {
      id: stream.id,
      page: 0,
      query: ""
    };

    dispatch(fetchUserAccess(request));
  }, [dispatch, stream.id]);

  useEffect(() => {
    if (updatedUserAccess) {
      handleClose && handleClose();
    }
  }, [updatedUserAccess]);

  useEffect(() => {
    if (_.size(users) > 0) {
      updateInitialUserAccess(users);
    }
  }, [users])

  const updateInitialUserAccess = (users: Users[]) => {
    const updatedAccessMap: { [key: string]: string } = {};

      users.forEach((item) => {
        const user = item.user || item.userGroup;
        const entityId = user?.id;

        if (entityId) {
          updatedAccessMap[entityId] = getWriteAccess(item) ? "edit" : getReadAccess(item) ? "read" : "";
        }
      });
      setUpdatedAccess(updatedAccessMap);
  }

  const getUserWriteAccess = (item: Users) => {

    const user = item.user || item.userGroup;

    if (user) {
      if (user.admin || user.id === _.get(stream, "createdBy.id")) {
        return true;
      }
    }

    return item.write || item.groupWrite ? true : false;
  }

  const getUserReadAccess = (item: Users) => {
      
      const user = item.user || item.userGroup;

      if (user) {
        if (user.admin || user.id === _.get(stream, "createdBy.id")) {
          return true;
        }
      }
  
      return item.read || item.groupRead ? true : false;
  }
  
  const resetUserAccess = (users: any, access: string) => {
    const updatedAccessMap: { [key: string]: string } = {};

    const adminUsers = users.filter((item) => {
      const user = item.user || item.userGroup;
      const role = getUserRole(user);
      return role === "Owner" || role === "Admin";
    });

    adminUsers.forEach((item) => {
      const user = item.user || item.userGroup;
      const entityId = user?.id;

      if (entityId) {
        updatedAccessMap[entityId] = "edit";
      }
    });

    const nonAdminUsers = users.filter((item) => {
      const user = item.user || item.userGroup;
      const role = getUserRole(user);
      return role !== "Owner" && role !== "Admin";
    });


    nonAdminUsers.forEach((item) => {
      const user = item.user || item.userGroup;
      const entityId = user?.id;

      if(entityId){
        if(access === "read"){
          setSelectedOption("");
          updatedAccessMap[entityId] =  getUserWriteAccess(item) ? "edit" : getUserReadAccess(item) ? "read" : "";
        } else if(access === "edit"){
          setSelectedOption("read");
          updatedAccessMap[entityId] =  getUserWriteAccess(item) ? "edit" : "read";
        }
      }
    });

    setUpdatedAccess(updatedAccessMap);
  }

  const handleUpdateStreamAccess = () => {
    const request = {
      stream: {
        id: stream.id,
        users: [...updatedUsers],
        userGroups: [...updatedUserGroups],
        readAll: selectedOption === "read" || selectedOption === "edit",
        writeAll: selectedOption === "edit"
      }
    };
    updateStreamAccessRequest(request).then(() => {
      trackUserAccessUpdated({});
    });
  };

  const updateUserAccess = (access: string, item: Users) => {
    const user = item.user || item.userGroup;
    const entityId = user?.id;

    let updatedEntity: Users = { ...item };

    if (entityId) {

      //check if original access has been updated already, then set reset it
      if (updatedAccess[entityId] === access) {
        if(access === "read") {
          const { [entityId]: _, ...filteredUpdatedAccess } = updatedAccess;
          setUpdatedAccess(filteredUpdatedAccess);

          updatedEntity = {
            ...item,
            read: false,
            write: false,
          };

        } else {
          setUpdatedAccess({
            ...updatedAccess,
            [entityId]: "read",
          });

          updatedEntity = {
            ...item,
            read: true,
            write: false,
          };
        }

      } else {
        //if access is not updated, then set it
        setUpdatedAccess({
          ...updatedAccess,
          [entityId]: access,
        });

        updatedEntity = {
          ...item,
          read: access === "read" || access === "edit",
          write: access === "edit",
        };
      }
    }

    if (item.entityType === "user") {
      setUpdatedUsers([
        ...updatedUsers.filter(u => u.user?.id !== entityId) as Users[],
        updatedEntity
      ]);
    } else if (item.entityType === "user_group") {
      setUpdatedUserGroups([
        ...updatedUserGroups.filter(g => g.userGroup?.id !== entityId),
        updatedEntity
      ]);
    }
  };


  const getReadAccess = (item: Users) => {

    const user = item.user || item.userGroup;
    
    if (selectedOption === "edit" || selectedOption === "read") {
      return true;
    }
    
    if (user) {
      if (user.admin || user.id === _.get(stream, "createdBy.id")) {
        return true;
      }
    }

    if (streamAccessControlStream.readAll) {
      return true;
    } 

    if (item.groupRead) {
      return true;
    }

    return item.write ? true : (item.read) ? true : false;

  };

  const getWriteAccess = (item: Users) => {

    const user = item.user || item.userGroup;

    if (selectedOption === "edit") {
      return true;
    }

    if (user) {
      if (user.admin || user.id === _.get(stream, "createdBy.id")) {
        return true;
      }
    }

    if (streamAccessControlStream.writeAll) {
      return true;
    } 

    if(selectedOption === "edit") {
      return true;
    }

    if (item.groupWrite) {
      return true;
    }

    return item.write ? true : false;
  }

  const bulkUpdateAccess = (access: string) => {
    const updatedUserAccess: { [key: string]: string } = {};
    
    const nonAdminUsers = users.filter((item) => {
      const user = item.user || item.userGroup;
      const role = getUserRole(user);
      return role !== "Owner" && role !== "Admin";
    });

    nonAdminUsers.forEach((userItem) => {
      const user = userItem.user || userItem.userGroup;
      if (user) {
       if(access === "read"){
        updatedUserAccess[user.id] = getUserWriteAccess(userItem) ? "edit" : "read";
       }
       if(access === "edit"){
        updatedUserAccess[user.id] = "edit";
       }
      } 
    });

    const adminUsers = users.filter((item) => {
      const user = item.user || item.userGroup;
      const role = getUserRole(user);
      return role === "Owner" || role === "Admin";
    });

    adminUsers.forEach((userItem) => {
      const user = userItem.user || userItem.userGroup;
      if (user) {
        updatedUserAccess[user.id] = "edit";
      }
    });

    setUpdatedAccess(updatedUserAccess);
  };

  const handleSearch = (e: any) => {
    e.preventDefault();
    const request = {
      id: stream.id,
      page: 0,
      query: query
    };
    dispatch(fetchStreamAccessUsers(request));
  };

  const getUserRole = (user: User) => {
    if (_.get(user, "id") === _.get(stream, "createdBy.id") || _.get(user, "createStream")) {
      return "Owner";
    } else if (_.get(user, "role") === "Administrator" || _.get(user, "admin")) {
      return "Admin";
    }
    else return;
  }

  const handleClear = () => {
    setQuery("");
    const request = {
      id: stream.id,
      page: 0,
      query: ""
    };
    dispatch(fetchStreamAccessUsers(request));
  }

  const onChange = (e: any) => {
    setQuery(e.target.value);
    const request = {
      id: stream.id,
      page: 0,
      query: e.target.value
    };
    dispatch(fetchStreamAccessUsers(request));
  }

  const renderSearch = () => (
    <div className={cx(styles.searchBar)}>
      <i className={cx(styles.icon, 'paperflite-search')}></i>
      <form onSubmit={(e) => handleSearch(e)}>
        <input type='text' placeholder='Type here to search...' value={query} onChange={(e) => onChange(e)} />
        {query && <i className={cx(styles.icon, styles.clear, 'paperflite-close')} onClick={handleClear}></i>}
      </form>
    </div>
  );

  const handleBulkUpdateAccess = (access: string) => {
    if(selectedOption === access){
      setUpdatedAccess({});
      setUpdatedUsers([]);
      setUpdatedUserGroups([]);
      resetUserAccess(users, access);
    } else {
      setSelectedOption(access);
      bulkUpdateAccess(access);
    }
  }

  const renderOptions = () => (
    <div className={styles.options}>
      <span
        className={styles.option}
        onClick={() => {
          if(selectedOption === "edit") {
            return
          }
          handleBulkUpdateAccess("read")
          }}>
        <i
          className={cx({
            [styles.disableAccess]: selectedOption === "edit",
            [styles.selectedAccess]: selectedOption === "read" || selectedOption === "edit",
            "fa-solid fa-check-circle": selectedOption === "read" || selectedOption === "edit",
            "paperflite-radio-button-unselected": selectedOption !== "read" && selectedOption !== "edit",
          })}
        ></i>
        <span className={styles.optionTitle}>All can view</span>
      </span>
      <span className={styles.borderLine}></span>
      <span
        className={styles.option}
        onClick={() => handleBulkUpdateAccess("edit")}>
        <i
          className={cx({
            [styles.selectedAccess]: selectedOption === "edit",
            "fa-solid fa-check-circle": selectedOption === "edit",
            "paperflite-radio-button-unselected": selectedOption !== "edit",
          })}
        ></i>
        <span className={styles.optionTitle}>All can edit</span>
      </span>
    </div>
  );

  const handleClick = (access: string, disableUserAccessUpdate: boolean, item: any) => {
    if(!disableUserAccessUpdate && selectedOption !== "edit") {
      updateUserAccess(access, item)
    }
  }

  const renderUserDetails = (item: Users) => {
    const user = item.user || item.userGroup;
    const isUser = item.entityType === "user" ? true : false;
    const isUserEnabled = isUser ? _.get(user, 'enabled', true) : true;
    const role = getUserRole(user);
    const disableUserAccessUpdate = role === "Owner" || role === "Admin";
    const isReadAccess = updatedAccess[user.id] === "read" || updatedAccess[user.id] === "edit";
    const isEditAccess = updatedAccess[user.id] === "edit";
    const name = isUser ? _.get(user, "firstName") + " " + _.get(user, "lastName"): _.get(user, "name", "");
    const isDeactivatedUser = isUser && !isUserEnabled;

    return (
      <div key={_.get(user, "id")} className={styles.userDetails}>
        <div
          className={cx(styles.user, {
            [styles.deactivatedUser]: isDeactivatedUser,
          })}
        >
          <i className={cx({
            "paperflite-user-settings": isUser && !isDeactivatedUser,
            "paperflite-group": !isUser,
            "fa fa-ban": isDeactivatedUser,
          })}></i>
          <span>
            <Tooltip placement="top" content={name}>
              <span>
                <Truncate lines={1} width={role ? 150 : 170} className={styles.name}>{name}</Truncate>
              </span>
            </Tooltip>
          </span>
          <span>
            {isDeactivatedUser && (
              <span className={styles.deactivatedTag}>(Deactivated)</span>
            )}
            {role && <span className={styles.role}>{role}</span>}
          </span>
        </div>
        {!isDeactivatedUser && (
        <span className={styles.userAccess}>
          <i className={cx(styles.access, {
            "paperflite-radio-button-unselected": !isReadAccess,
            "fa-solid fa-check-circle": isReadAccess,
            [styles.selectedAccess]: updatedAccess[user.id] === "read",
            [styles.disableAccess]: disableUserAccessUpdate || isEditAccess || selectedOption === "read" || item.groupRead,
          })}
            onClick={() => {
              if(selectedOption === "read") {
                return
              }
              handleClick("read", disableUserAccessUpdate, item)
            }}
          ></i>
          <span> view </span>
          <span className={styles.borderLine}></span>
          <i className={cx(styles.access, {
            "paperflite-radio-button-unselected": !isEditAccess,
            "fa-solid fa-check-circle": isEditAccess,
            [styles.selectedAccess]: isEditAccess,
            [styles.disableAccess]: disableUserAccessUpdate || selectedOption === "edit" || item.groupWrite
          })}
            onClick={() => handleClick("edit", disableUserAccessUpdate, item)}
          ></i>
          <span> edit </span>
        </span>
        )}
      </div>
    );
  }

  const handleLoadMoreUsers = () => {
    if (currentPage + 1 <= totalPages) {
      dispatch(fetchStreamAccessUsers({ id: stream.id, page: currentPage, query: query }));
    }
  }

  const renderUsers = () => {
    return (
      <InfiniteScroll
        className={styles.users}
        hasMore={loadMore}
        loadMore={handleLoadMoreUsers}
        loading={loadingUsers}
        loader={() => {
          return (
            <>
            <ManageUserAccessPlaceholder width = "70%"/>
            <ManageUserAccessPlaceholder width = "50%"/>
            </>
          );
        }}>
        {_.size(users) === 0 && !loadingUsers && renderEmptyMessage()}
        {users.map((item) => renderUserDetails(item))}
      </InfiniteScroll>
    );
  };

  const renderActions = () => {
    return (
      <>
        <Button icon="paperflite-tick"
          text={confirming ? 'Updating...' : 'Update'}
          variant='primary'
          disabled={confirming}
          onClick={handleUpdateStreamAccess}
        />
        <Button text="Cancel"
          variant='secondary'
          disabled={confirming}
          onClick={handleClose}
        />
      </>
    )
  }

  const renderEmptyMessage = () => {
    return (
      <PlaceholderEmptyMessage
        title="Uh oh!"
        message= 'The user(s) that you were looking for was not found.'
        image={StreamAccessEmptyMessageSvgIcon}
        className={styles.emptyMessage}
        onClick={''}
        visible={true}
      />
    );
  };

  const renderContent = () => {
    return (
      <>
        <div className={styles.wrappedContainer}>
          {renderOptions()}
          {renderSearch()}
        </div>
        {_.size(stream) > 0 && renderUsers()}
        {_.size(users) > 0 && !openUserAccess && (
          <div className={styles.actionsContainer}>
          {renderActions()}
          </div>
        )}
      </>
    )
  }

  const renderSlider = () => {
    return (
      <Slider.Dialog className={styles.cloneConfirmDialog} width="300" show={true} onClose={handleClose || (() => {})}>
        <Slider.Header
          title="manage user access"
          message="Decide who gets access. Remember, with great power comes great responsibility."
        >
        </Slider.Header>
        <Slider.Body>
          {renderContent()}
        </Slider.Body>
        <Slider.Actions>
          {renderActions()}
        </Slider.Actions>
      </Slider.Dialog>
    )
  }

  return openUserAccess ? renderSlider() :
    <div className={styles.content}>
      {_.size(stream) === 0 ? <div className={styles.loadingContent}>Loading users</div> : renderContent()}
    </div>
};

export default ManageUserAccess;

interface ManageUserAccessPlaceholderProps {
  width: string | number;
}

const ManageUserAccessPlaceholder: React.FC<ManageUserAccessPlaceholderProps> = ({ width }) => {
  return (
    <div className={cx(styles.placeholder)} style={{ width: width }}>
    <div className={cx(styles.user, styles.block)}></div>
  </div>
  );
};